import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { DatePickerModule } from 'primeng/datepicker';
import { ActivatedRoute } from '@angular/router';

import { ApiService } from '../../services/api.service';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../shared/constants/pagination.constants';

@Component({
    selector: 'app-demo-schedule',
    imports: [CommonModule, FormsModule, FontAwesomeModule, DatePickerModule],
    templateUrl: './demo-schedule.component.html',
    styleUrls: ['../lead-management.shared.css']
})
export class DemoScheduleComponent implements OnInit {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  demos: any[] = [];
  loading = false;
  currentPage = 1;
  pageSize = 10;
  totalRecords = 0;
  totalPages = 1;
  nextPage: string | null = null;
  previousPage: string | null = null;
  selectedDemo: any = null;
  showEditModal = false;
  showConfirmationDialog: boolean = false;
  demo_data: any;

  demoForm: any = {
    demo_datetime: '',
    demo_type: 'ONLINE',
    demo_status: 'SCHEDULED',
    demo_notes: '',
    remind_one_hour_before: false,
  };

  filters: any = {
    search: '',
    date: null,
    status: '',
  };

  statuses = [
    ['SCHEDULED', 'Scheduled'],
    ['COMPLETED', 'Completed'],
    ['CANCELLED', 'Cancelled'],
  ];

  demoTypes = [
    ['ONLINE', 'Online'],
    ['ON_SITE', 'On-site'],
  ];


  constructor(
    private apiService: ApiService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    library: FaIconLibrary,
  ) {
    library.addIcons(faPen, faTrash);
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      this.filters = {
        search: params.get('search') || '',
        date: this.parseDate(params.get('date')),
        status: params.get('status') || '',
      };
      this.currentPage = Number(params.get('page') || 1);
      this.loadDemos();
    });
  }

  loadDemos(): void {
    this.loading = true;
    this.apiService.listLeadDemos(this.currentPage, this.pageSize, this.filters).subscribe({
      next: (response: any) => {
        this.demos = response?.results || [];
        this.totalRecords = response?.count || 0;
        this.totalPages = Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
        this.nextPage = response?.next || null;
        this.previousPage = response?.previous || null;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadDemos();
  }

  resetFilters(): void {
    this.filters = {
      search: '',
      date: null,
      status: '',
    };
    this.currentPage = 1;
    this.loadDemos();
  }

  changePage(step: number): void {
    const nextPage = this.currentPage + step;
    if (nextPage < 1 || nextPage > this.totalPages) {
      return;
    }
    this.currentPage = nextPage;
    this.loadDemos();
  }

  next(): void {
    if (!this.nextPage) {
      return;
    }
    this.currentPage++;
    this.loadDemos();
  }

  previous(): void {
    if (!this.previousPage) {
      return;
    }
    this.currentPage--;
    this.loadDemos();
  }

  selectPageSize(): void {
    this.currentPage = 1;
    this.loadDemos();
  }

  openEdit(demo: any): void {
    this.selectedDemo = demo;
    this.demoForm = {
      demo_datetime: this.parseDate(demo.demo_datetime),
      demo_type: demo.demo_type,
      demo_status: demo.demo_status,
      demo_notes: demo.demo_notes || '',
      remind_one_hour_before: !!demo.remind_one_hour_before,
    };
    this.showEditModal = true;
  }

  saveDemo(): void {
    if (!this.selectedDemo?.id || !this.selectedDemo?.lead) {
      return;
    }
    this.apiService.updateLeadDemo(this.selectedDemo.lead, this.selectedDemo.id, this.serializeDemoForm(this.demoForm)).subscribe({
      next: () => {
        this.toastr.success('Demo updated successfully.', 'Success');
        this.showEditModal = false;
        this.loadDemos();
      },
      error: (err) => {
        const message = err?.error?.detail || 'Unable to update demo.';
        this.toastr.error(message, 'Error');
      }
    });
  }
 
  deleteDemo(): void {
    if (!this.demo_data?.id || !this.demo_data?.lead || !window.confirm(`Delete demo for ${this.demo_data.customer_name}?`)) {
      return;
    }
    this.apiService.deleteLeadDemo(this.demo_data.lead, this.demo_data.id).subscribe({
      next: () => {
        this.toastr.success('Demo deleted successfully.', 'Success');
        if (this.demos.length === 1 && this.currentPage > 1) {
          this.currentPage--;
        }
        this.loadDemos();
      },
      error: (err) => {
        const message = err?.error?.detail || 'Unable to delete demo.';
        this.toastr.error(message, 'Error');
      }
    });
  }

 openConfirmationDialog(demo: any) {
    this.showConfirmationDialog = true;
    this.demo_data = demo;
  }

  closeConfirmationDialog() {
    this.showConfirmationDialog = false;
  }


  private parseDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date;
  }

  private serializeDemoForm(form: any): any {
    return {
      ...form,
      demo_datetime: form.demo_datetime ? formatDate(form.demo_datetime, "yyyy-MM-dd'T'HH:mm:ss", 'en-IN') : null,
    };
  }
}
